import { useState } from 'react';

import { Icon } from '@iconify/react';
import PropTypes from 'prop-types';

/**
 * Tool icons mapping
 */
const TOOL_ICONS = {
  google_search: 'mdi:magnify',
  search_web: 'mdi:magnify',
  document_search: 'mdi:file-search-outline',
  search_documents: 'mdi:file-search-outline',
  fetch_url: 'mdi:link-variant',
  fetch_web_page: 'mdi:link-variant',
  execute_code: 'mdi:code-braces',
  get_file: 'mdi:file-download-outline',
  browse_web: 'mdi:web',
  save_memory: 'mdi:brain',
  save_to_knowledge_base: 'mdi:brain',
  read_knowledge_base_page: 'mdi:book-open-page-variant-outline',
  search_knowledge_base: 'mdi:book-search-outline',
  list_knowledge_base: 'mdi:bookshelf',
  delete_knowledge_base_page: 'mdi:brain-off-outline',
  record_learning: 'mdi:school-outline',
  record_error: 'mdi:alert-circle-outline',
  create_skill: 'mdi:puzzle-outline',
  manage_scheduled_task: 'mdi:calendar-clock',
  run_skill: 'mdi:play-circle-outline',
  default: 'mdi:function',
};

/**
 * Tool display names
 */
const TOOL_NAMES = {
  google_search: 'Web Search',
  search_web: 'Web Search',
  document_search: 'Document Search',
  search_documents: 'Document Search',
  fetch_url: 'Fetch URL',
  fetch_web_page: 'Fetch Web Page',
  execute_code: 'Code Execution',
  get_file: 'Download File',
  browse_web: 'Browse Web',
  save_memory: 'Save Memory',
  save_to_knowledge_base: 'Update Corporate Memory',
  read_knowledge_base_page: 'Consult Corporate Memory',
  search_knowledge_base: 'Search Corporate Memory',
  list_knowledge_base: 'Review Corporate Memory',
  delete_knowledge_base_page: 'Forget Corporate Memory',
  record_learning: 'Record Learning',
  record_error: 'Record Error',
  create_skill: 'Create Skill',
  manage_scheduled_task: 'Manage Scheduled Task',
  run_skill: 'Run Skill',
};

/**
 * Format tool arguments for display
 */
function formatArgs(args) {
  if (!args) return null;

  if (typeof args === 'string') {
    return args;
  }

  // For search tools, show the query
  if (args.query) {
    return args.query;
  }

  // For fetch_url, show the URL
  if (args.url) {
    return args.url;
  }

  return JSON.stringify(args, null, 2);
}

/**
 * Render search results for display
 */
function SearchResults({ results }) {
  if (!results || !Array.isArray(results) || results.length === 0) {
    return <p className="text-gray-500 text-xs">No results found</p>;
  }

  return (
    <div className="space-y-2">
      {results.slice(0, 3).map((result, index) => (
        <div key={index} className="border-l-2 border-gray-200 pl-2">
          <p className="text-xs font-medium text-gray-800 line-clamp-1">
            {result.title || 'Untitled'}
          </p>
          {result.snippet && (
            <p className="text-xs text-gray-500 line-clamp-2">{result.snippet}</p>
          )}
          {result.url && (
            <a
              href={result.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-500 hover:underline cursor-pointer"
            >
              {(() => {
                try {
                  return new URL(result.url).hostname;
                } catch {
                  return result.url;
                }
              })()}
            </a>
          )}
        </div>
      ))}
      {results.length > 3 && (
        <p className="text-xs text-gray-400">+{results.length - 3} more results</p>
      )}
    </div>
  );
}

SearchResults.propTypes = {
  results: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      snippet: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
};

/**
 * Render document search results
 */
function DocumentResults({ results }) {
  if (!results || !Array.isArray(results) || results.length === 0) {
    return <p className="text-gray-500 text-xs">No documents found</p>;
  }

  return (
    <div className="space-y-2">
      {results.slice(0, 3).map((result, index) => (
        <div key={index} className="border-l-2 border-purple-200 pl-2">
          <div className="flex items-center gap-1">
            <Icon icon="mdi:file-document-outline" className="size-3 text-purple-500" />
            <p className="text-xs font-medium text-gray-800 line-clamp-1">
              {result.source || 'Document'}
            </p>
            {result.score && (
              <span className="text-xs text-gray-400">
                ({Math.round(result.score * 100)}%)
              </span>
            )}
          </div>
          {result.content && (
            <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{result.content}</p>
          )}
        </div>
      ))}
      {results.length > 3 && (
        <p className="text-xs text-gray-400">+{results.length - 3} more results</p>
      )}
    </div>
  );
}

DocumentResults.propTypes = {
  results: PropTypes.arrayOf(
    PropTypes.shape({
      source: PropTypes.string,
      content: PropTypes.string,
      score: PropTypes.number,
      page: PropTypes.number,
    }),
  ),
};

/**
 * ToolCallCard component for displaying tool invocations
 */
export function ToolCallCard({ toolCall }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toolName = toolCall.tool_name || toolCall.name || 'unknown';
  const icon = TOOL_ICONS[toolName] || TOOL_ICONS.default;
  const displayName = TOOL_NAMES[toolName] || toolName;
  const toolArgs = toolCall.arguments || toolCall.args;
  const formattedArgs = formatArgs(toolArgs);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={toggleExpanded}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-100 cursor-pointer transition-colors"
      >
        <Icon icon={icon} className="size-4 text-gray-600 flex-shrink-0" />
        <span className="text-xs font-medium text-gray-700">{displayName}</span>

        {formattedArgs && (
          <span className="text-xs text-gray-500 truncate flex-1">
            {typeof formattedArgs === 'string' && formattedArgs.length > 50
              ? `"${formattedArgs.slice(0, 50)}..."`
              : `"${formattedArgs}"`}
          </span>
        )}

        <Icon
          icon={isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
          className="size-4 text-gray-400 flex-shrink-0"
        />
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 py-2 border-t border-gray-200 bg-white">
          {/* Arguments */}
          {toolArgs && (
            <div className="mb-2">
              <p className="text-xs font-medium text-gray-500 mb-1">Input</p>
              <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                {typeof toolArgs === 'string'
                  ? toolArgs
                  : JSON.stringify(toolArgs, null, 2)}
              </pre>
            </div>
          )}

          {/* Results */}
          {toolCall.result !== undefined && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Results</p>
              {toolName === 'google_search' && Array.isArray(toolCall.result) ? (
                <SearchResults results={toolCall.result} />
              ) : toolName === 'document_search' && Array.isArray(toolCall.result) ? (
                <DocumentResults results={toolCall.result} />
              ) : (
                <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto max-h-40">
                  {typeof toolCall.result === 'string'
                    ? toolCall.result.slice(0, 500) +
                      (toolCall.result.length > 500 ? '...' : '')
                    : JSON.stringify(toolCall.result, null, 2).slice(0, 500)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

ToolCallCard.propTypes = {
  toolCall: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    args: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    result: PropTypes.any,
  }).isRequired,
};
